import {
	ChangeDetectionStrategy,
	Component,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { AppDispatch } from '@state/app/app.dispatch';
import { AuthDispatch } from '@state/auth/auth.dispatch';
import {
	LeapApiService,
	LeapOnboardingStatus,
	LeapOnboardingStatusResponse,
} from '../leap-api.service';
import { LeapSdkService } from '../leap-sdk.service';
import { Select } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthState } from '@state/auth';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AuthUser } from '@state/auth/auth.models';
import {
	LeapOnboardingScreen,
	LeapOnboardingService,
} from '../leap-onboarding.service';
import { Logger } from '@core/logger';
import { LeapOnboardingDialogData } from './entry-point.types';
import { ActivatedRoute } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';

@UntilDestroy()
@Component({
	selector: 'app-leap-entry-point',
	templateUrl: './entry-point.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,

	standalone: false,
})
export class LeapEntryPointComponent implements OnInit, OnDestroy {
	@Select(AuthState.loginChecked) loginChecked$: Observable<boolean>;
	@Select(AuthState.getLoggedInUser) loggedInUser$: Observable<AuthUser>;

	public showLoadingIcon$: BehaviorSubject<boolean>;
	public currentOnboardingStep$: BehaviorSubject<LeapOnboardingScreen>;
	public isLoading$: BehaviorSubject<boolean>;

	public dialogData: LeapOnboardingDialogData;
	private onboardingStatusResponse: LeapOnboardingStatusResponse;
	public showLoadingMessage$: BehaviorSubject<string>;

	/**
	 * Constructs a new instance of the LeapRedirectComponent class.
	 * @param appDispatch The app dispatch service.
	 * @param leapSDKService The Leap SDK service.
	 * @param authDispatch The auth dispatch service.
	 * @param leapApiService The Leap API service.
	 * @param dialog
	 * @param onboardingService
	 * @param activatedRoute
	 */
	constructor(
		public appDispatch: AppDispatch,
		public leapSDKService: LeapSdkService,
		private authDispatch: AuthDispatch,
		private leapApiService: LeapApiService,
		private dialog: MatDialog,
		private onboardingService: LeapOnboardingService,
		private activatedRoute: ActivatedRoute,
		private auth: AngularFireAuth,
	) {}

	async ngOnInit() {
		this.showLoadingIcon$ = this.onboardingService.showLoadingIcon$;
		this.showLoadingMessage$ = this.onboardingService.showLoadingMessage$;
		this.currentOnboardingStep$ = this.onboardingService.currentOnboardingStep$;
		this.isLoading$ = this.onboardingService.isLoading$;
		const unsubscribe = await this.auth.onAuthStateChanged((user) => {
			Logger.info(
				'entry-point.component - Entered onAuthStateChanged handler.',
			);
			if (!user) {
				unsubscribe();
				Logger.info(
					'entry-point.component - In onAuthStateChanged handler logout has completed we can now proceed.',
				);
				this.continueInitAfterLogOut();
			}
		});

		// We are going to logout to ensure there is no garbage left from previous sessions.
		this.authDispatch.LogOut();
		this.authDispatch.SetLoginChecked(false);
		this.appDispatch.SetLoadedFromLeap(true);
	}

	private async continueInitAfterLogOut(): Promise<void> {
		const leapOnboardingStatusResponse =
			await this.getOnboardingStatusResponse();
		this.onboardingStatusResponse = leapOnboardingStatusResponse;

		const queryParams = this.activatedRoute.snapshot.queryParams;

		const { page: redirectTo, requestId } = queryParams;
		await this.onboardingService.init(
			redirectTo,
			requestId,
			leapOnboardingStatusResponse,
		);

		// If user and firm are onboarded (FullyOnboarded), then redirect user immediately
		const fullyOnboarded =
			leapOnboardingStatusResponse.leapOnboardingStatus ===
			LeapOnboardingStatus.FullyOnboarded;
		if (fullyOnboarded) {
			this.showLoadingMessage$.next('User already onboarded...');
			this.showLoadingMessage$.next('Connecting to Closer...');
			await this.onboardingService.connect(null, null);
			return;
		}

		/* Following code will run when LeapOnboardingStatus is not FullyOnboarded */
		this.subscribeToOnboardingStepChanges();

		this.onboardingService.goToVideoIntroScreen();

		this.onboardingService.showLoadingIcon$.next(false);
	}

	ngOnDestroy() {
		this.onboardingService.unsubCustomTokenHandlerSub();
		this.dialog.closeAll();
	}

	private subscribeToOnboardingStepChanges() {
		this.dialogData = {
			onboardingStatusResponse: this.onboardingStatusResponse,
		};

		// React to changes in the current onboarding stage and load the corresponding component
		this.currentOnboardingStep$
			.pipe(untilDestroyed(this))
			.subscribe((currentStageDetails) => {
				this.dialog.closeAll();

				const dialogConfig: MatDialogConfig<LeapOnboardingDialogData> = {
					disableClose: true,
					hasBackdrop: false,
					data: this.dialogData,
				};

				if (currentStageDetails.panelClass) {
					dialogConfig.panelClass = currentStageDetails.panelClass;
				}

				this.dialog.open(currentStageDetails.component, dialogConfig);
			});
	}

	// execute this after getLeapSdkAndExtractTokens() runs
	private async getOnboardingStatusResponse() {
		try {
			this.showLoadingMessage$.next('Checking onboarding status...');
			const userInfo = await this.leapSDKService.getUserInfo();
			const accessToken = await this.leapSDKService.getAccessToken();
			const onboardingStatusResponse = await firstValueFrom(
				this.leapApiService.getOnboardingStatus(
					userInfo.userId,
					userInfo.firmId,
					accessToken,
				),
			);

			return onboardingStatusResponse;
		} catch (err) {
			this.showLoadingMessage$.next(
				'Error occurred during checking onboarding status...',
			);
			Logger.error(
				`An unexpected error occurred in setOnboardingStatusResponse. ${JSON.stringify(err)}`,
			);
		}
	}
}
